import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ObjectTypeInfo } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ParameterDefinition, ParameterType } from '@barthauer/abc/queries/parameters/core';
import { AbcDropDownBoxComponent, AbcTextBoxComponent } from '@barthauer/abc/ui-components';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { TextEditorButton, ValidationCallbackData } from 'devextreme/common';
import { ValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { ListDataSource } from 'libs/abc/arena/src/lib/core/models/data.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DefinitionsEditorService } from '../definitions-editor.service';
import { parameterTypeDatasource, Translation } from '../models/definitions-editor.viewModel';

@Component({
  selector: 'parameters-definitions-editor-container',
  templateUrl: './definitions-editor-container.component.html',
  styleUrls: ['./definitions-editor-container.component.scss'],
})
export class DefinitionsEditorContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('parameterValueDropDownBox') parameterValueDropDownBox: AbcDropDownBoxComponent;
  @ViewChild('valueSelectionExpressionDropDownBox') valueSelectionExpressionDropDownBox: AbcDropDownBoxComponent;
  @ViewChild('nameValidationRule') nameValidationRule: DxiValidationRuleComponent;
  @ViewChild('nameTextBox') nameTextBox: AbcTextBoxComponent;
  @ViewChild('captionTextBox') captionTextBox: AbcTextBoxComponent;

  @Input() parameterDefinition: ParameterDefinition;
  @Input() parameterDefinitions: ParameterDefinition[] = [];
  @Input() objectTypesInfo: ObjectTypeInfo[] = [];
  @Input() pickListByNameDataSource: ListDataSource;
  @Input() pickListByColumnDataSource: { Name: string; DisplayText: string }[];
  @Input() initialized: boolean = false;
  @Input() cancelChanges: (parameterDefinitionsBackup?: ParameterDefinition[]) => void;
  @Input() saveChanges: () => void;

  public res: ResourceSet;

  protected translation: Translation;
  protected parameterTypeDatasource = parameterTypeDatasource;
  protected ParameterType = ParameterType;
  protected parameterDefinitionsBackup: ParameterDefinition[];
  protected defaultParameterValueButtons: (string | TextEditorButton)[] = [
    {
      location: 'after',
      name: 'drop',
      options: {
        icon: 'spindown',
        stylingMode: 'text',
        onClick: () =>
          this.parameterValueDropDownBox.opened
            ? this.parameterValueDropDownBox.instance.close()
            : this.parameterValueDropDownBox.instance.open(),
      },
    },
  ];
  protected objectSelectionButtons: (string | TextEditorButton)[] = [
    {
      location: 'after',
      name: 'open-expression',
      options: {
        icon: 'more',
        stylingMode: 'text',
        onClick: () =>
          this.definitionsEditorService.openExpressionEditor(this.parameterDefinition, this.parameterDefinitions),
      },
    },
  ];
  protected queryButtons: (string | TextEditorButton)[] = [
    {
      location: 'after',
      name: 'open-bce',
      options: { icon: 'more', stylingMode: 'text', onClick: () => this.definitionsEditorService.openBce() },
    },
  ];
  protected valueSelectionButtons: (string | TextEditorButton)[] = [
    {
      location: 'after',
      name: 'drop',
      options: {
        icon: 'spindown',
        stylingMode: 'text',
        onClick: () =>
          this.valueSelectionExpressionDropDownBox.opened
            ? this.valueSelectionExpressionDropDownBox.instance.close()
            : this.valueSelectionExpressionDropDownBox.instance.open(),
      },
    },
    {
      location: 'after',
      name: 'open-expression',
      options: {
        icon: 'more',
        stylingMode: 'text',
        onClick: () =>
          this.definitionsEditorService.openExpressionEditor(this.parameterDefinition, this.parameterDefinitions),
      },
    },
  ];

  private $unsubscribe: Subject<void> = new Subject();
  constructor(
    private resourcesService: ResourceManagerService,
    private definitionsEditorService: DefinitionsEditorService
  ) {}
  async ngOnInit(): Promise<void> {
    this.res = await this.resourcesService.getResourceSet('abc-parameters-definitions-editor');

    this.translation = {
      title: this.res.get('parameter-title'),
      save: this.res.get('parameter-save'),
      cancel: this.res.get('parameter-cancel'),
      name: this.res.get('parameter-name'),
      newParameter: this.res.get('parameter-new'),
      nameUnique: this.res.get('parameter-nameUnique'),
      nameRequired: this.res.get('parameter-nameRequired'),
      nameNumeric: this.res.get('parameter-nameNumeric'),
      label: this.res.get('parameter-label'),
      description: this.res.get('parameter-description'),
      type: this.res.get('parameter-type'),
      defaultValue: this.res.get('parameter-defaultValue'),
      requiredInput: this.res.get('parameter-requiredInput'),
      alwaysDefaultValue: this.res.get('parameter-alwaysDefaultValue'),
      editMask: this.res.get('parameter-formatExpression'),
      minValue: this.res.get('parameter-minValue'),
      maxValue: this.res.get('parameter-maxValue'),
      invalidValue: this.res.get('parameter-invalidValue'),
      yes: this.res.get('parameter-yes'),
      no: this.res.get('parameter-no'),
      multiSelection: this.res.get('parameter-multiSelection'),
      filterCondition: this.res.get('parameter-filterCondition'),
      cSharpExpression: this.res.get('parameter-cSharpExpression'),
      cSharpExpressionRequired: this.res.get('parameter-cSharpExpression-required'),
      columnExpression: this.res.get('parameter-columnExpression'),
      column: this.res.get('parameter-column'),
    };

    this.parameterDefinitionsBackup = JSON.parse(JSON.stringify(this.parameterDefinitions));
  }

  async ngAfterViewInit(): Promise<void> {
    this.preapareParameterValueDropDownBox(this.parameterDefinition.Type);

    this.prepareValueSelectionDropDownBox(
      this.parameterDefinition.DataArgument,
      this.parameterDefinition.DataArgument2
    );

    this.captionTextBox.onFocusIn.pipe(takeUntil(this.$unsubscribe)).subscribe(() => {
      !this.captionTextBox.value?.trim().length && (this.captionTextBox.value = this.nameTextBox.value);
    });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  private preapareParameterValueDropDownBox(type: ParameterType): void {
    switch (type) {
      case ParameterType.DataAccessByObjectType:
        setTimeout(() => {
          this.parameterValueDropDownBox.setupForArrayData(
            this.objectTypesInfo,
            'Name',
            'UniqueDisplayText',
            [
              { dataField: 'UniqueDisplayText', allowFiltering: true, allowSearch: true },
              { dataField: 'Name', allowFiltering: true, allowSearch: true },
            ],
            this.parameterDefinition.DataArgument
          );
        }, 0);
        break;
      case ParameterType.DataAccessByValue:
        setTimeout(() => {
          this.parameterValueDropDownBox.setupForArrayData(
            this.objectTypesInfo,
            'Name',
            'DisplayText',
            [{ dataField: 'DisplayText', allowFiltering: true, allowSearch: true }],
            this.parameterDefinition.DataArgument
          );

          this.parameterValueDropDownBox.onValueChanged
            .pipe(takeUntil(this.$unsubscribe))
            .subscribe(async (event: ValueChangedEvent) => {
              this.prepareValueSelectionDropDownBox(event.value, this.parameterDefinition.DataArgument2);
            });
        }, 0);

        break;
      case ParameterType.PicklistByName:
        setTimeout(() => {
          this.parameterValueDropDownBox.setupForArrayData(
            this.pickListByNameDataSource.Data,
            'Name',
            'Designation',
            [{ dataField: 'Designation', allowFiltering: true, allowSearch: true }],
            this.parameterDefinition.DataArgument
          );
        }, 0);
        break;

      case ParameterType.PicklistByColumn:
        setTimeout(() => {
          this.parameterValueDropDownBox.setupForArrayData(
            this.pickListByColumnDataSource,
            'Name',
            'DisplayText',
            [{ dataField: 'DisplayText', allowFiltering: true, allowSearch: true }],
            `${this.parameterDefinition.DataArgument + '.' + this.parameterDefinition.DataArgument2}`
          );
        }, 0);
        break;

      case ParameterType.Query:
        // setTimeout(() => {
        //   this.parameterValueDropDownBox.buttons = [
        //     {
        //       location: 'after',
        //       name: 'open-bce',
        //       options: { icon: 'more', onClick: () => this.definitionsEditorService.openBce() },
        //     },
        //   ];
        // }, 0);
        break;

      default:
        break;
    }
  }

  private prepareValueSelectionDropDownBox(objectTypeName: string, columnName: string) {
    this.valueSelectionExpressionDropDownBox &&
      this.valueSelectionExpressionDropDownBox.setupForArrayData(
        this.objectTypesInfo.find((_: ObjectTypeInfo) => _.Name === objectTypeName)?.Columns ?? [],
        'Name',
        'DisplayText',
        [{ dataField: 'DisplayText', allowFiltering: true, allowSearch: true }],
        columnName
      );
  }

  public nameValidator = (params: ValidationCallbackData): Promise<void> => {
    const pattern = /^(?!\d+$)[A-Za-z0-9]+$/;

    const occurence = this.parameterDefinitions.filter(
      (_) => _.Name.toLocaleLowerCase() === params.value.toLocaleLowerCase()
    ).length;

    return new Promise((resolve, reject) => {
      if (!params.value.length) {
        this.nameValidationRule.message = this.translation.nameRequired;
        reject();
      } else if (!pattern.test(params.value)) {
        this.nameValidationRule.message = this.translation.nameNumeric;
        reject();
      } else if (occurence > 0) {
        this.nameValidationRule.message = this.translation.nameUnique;
        reject();
      } else {
        resolve();
      }
    });
  };

  public minNumberValidator = (params: ValidationCallbackData): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (params.value > this.parameterDefinition.MaxNumberValue) {
        reject();
      } else {
        resolve();
      }
    });
  };

  public maxNumberValidator = (params: ValidationCallbackData): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (params.value < this.parameterDefinition.MinNumberValue) {
        reject();
      } else {
        resolve();
      }
    });
  };

  public minDateValidator = (params: ValidationCallbackData): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (params.value > this.parameterDefinition.MaxDateValue) {
        reject();
      } else {
        resolve();
      }
    });
  };

  public maxDateValidator = (params: ValidationCallbackData): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (params.value < this.parameterDefinition.MinDateValue) {
        reject();
      } else {
        resolve();
      }
    });
  };

  public confirmChanges = (e: SubmitEvent): void => {
    e.preventDefault();
    this.saveChanges();
  };
}
